import { useEffect, useMemo, useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Spinner, useToast } from '../components/ui';
import { api } from '../lib/api';
import type { Template, TemplateOptions, TemplateTokens } from '../lib/types';

const COLOURS: Array<[keyof TemplateTokens, string, string]> = [
  ['green', 'Main colour', 'Headings, text and dark blocks'],
  ['cream', 'Paper colour', 'Page background'],
  ['lime', 'Accent colour', 'Highlights, stats bar text, lines'],
];

export function TemplateEditPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const [template, setTemplate] = useState<Template | null>(null);
  const [options, setOptions] = useState<TemplateOptions | null>(null);
  const [draft, setDraft] = useState<{ name: string; tokens: TemplateTokens; pageOrder: string[] } | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.getTemplate(id), api.templateOptions()])
      .then(([t, o]) => {
        setTemplate(t);
        setOptions(o);
        setDraft({ name: t.name, tokens: t.tokens, pageOrder: t.pageOrder });
      })
      .catch((e) => toast.push((e as Error).message, true));
  }, [id]);

  const dirty = useMemo(() => !!template && !!draft && JSON.stringify({ name: template.name, tokens: template.tokens, pageOrder: template.pageOrder }) !== JSON.stringify(draft), [template, draft]);

  // refresh the preview shortly after the last change
  const previewSrc = useMemo(() => (draft ? api.templatePreviewUrl(id, draft) : ''), [id, draft, previewKey]);
  const [debouncedSrc, setDebouncedSrc] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSrc(previewSrc), 600);
    return () => window.clearTimeout(t);
  }, [previewSrc]);

  if (!template || !options || !draft) return <div className="row"><Spinner /> <span className="sub">Loading…</span></div>;

  const locked = template.builtIn;
  const labelOf = (sectionId: string) => options.sections.find((s) => s.id === sectionId)?.label ?? sectionId;

  const move = (index: number, delta: number) => {
    const order = [...draft.pageOrder];
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    [order[index], order[target]] = [order[target], order[index]];
    setDraft({ ...draft, pageOrder: order });
  };

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.updateTemplate(template.id, draft);
      setTemplate(saved);
      setDraft({ name: saved.name, tokens: saved.tokens, pageOrder: saved.pageOrder });
      setPreviewKey((k) => k + 1);
      toast.push(`Saved as v${saved.version}`);
    } catch (e) {
      toast.push((e as Error).message, true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <NavLink to="/templates" className="btn small ghost">← Templates</NavLink>
        <h1 className="title">{template.name}</h1>
        <span className="sub">v{template.version} · used by {template.usedBy}</span>
        <div style={{ flex: 1 }} />
        {!locked && (
          <>
            {dirty && <button className="btn ghost" onClick={() => setDraft({ name: template.name, tokens: template.tokens, pageOrder: template.pageOrder })}>Discard</button>}
            <button className="btn primary" disabled={!dirty || saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save template'}</button>
          </>
        )}
      </div>

      {locked && (
        <div className="notice" style={{ marginBottom: 16 }}>
          This is the built-in template and can’t be changed. Duplicate it from the Templates page to make your own version.
        </div>
      )}
      {!locked && template.usedBy > 0 && (
        <div className="notice" style={{ marginBottom: 16 }}>
          {template.usedBy} booklet{template.usedBy === 1 ? '' : 's'} use this template — saving changes how they look the next time they are previewed or exported.
        </div>
      )}

      <div className="split">
        <div>
          <div className="panel">
            <h2 className="section">Name</h2>
            <div className="field">
              <input value={draft.name} disabled={locked} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
          </div>

          <div className="panel">
            <h2 className="section">Colours</h2>
            {COLOURS.map(([key, label, help]) => (
              <div className="colour-row" key={key}>
                <input
                  type="color"
                  value={draft.tokens[key]}
                  disabled={locked}
                  onChange={(e) => setDraft({ ...draft, tokens: { ...draft.tokens, [key]: e.target.value.toUpperCase() } })}
                />
                <div style={{ flex: 1 }}>
                  <div>{label}</div>
                  <div className="sub" style={{ fontSize: 12 }}>{help}</div>
                </div>
                <code>{draft.tokens[key]}</code>
              </div>
            ))}
            <div className="sub" style={{ fontSize: 12, marginTop: 8 }}>
              The sketches, stamps and tape strips are artwork from the PPT and keep their own colours.
            </div>
          </div>

          <div className="panel">
            <h2 className="section">Fonts</h2>
            <div className="grid-2">
              <div className="field">
                <label>Headings</label>
                <select value={draft.tokens.display} disabled={locked} onChange={(e) => setDraft({ ...draft, tokens: { ...draft.tokens, display: e.target.value } })}>
                  {options.displayFonts.map((f) => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Body text</label>
                <select value={draft.tokens.body} disabled={locked} onChange={(e) => setDraft({ ...draft, tokens: { ...draft.tokens, body: e.target.value } })}>
                  {options.bodyFonts.map((f) => <option key={f}>{f}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="panel">
            <h2 className="section">Page order</h2>
            <div className="sub" style={{ marginBottom: 10 }}>The cover is always first and the closing page always last.</div>
            <div className="order-list">
              <div className="order-item fixed">Cover</div>
              {draft.pageOrder.map((sectionId, i) => (
                <div className="order-item" key={sectionId}>
                  <span className="n">{i + 1}</span>
                  <span style={{ flex: 1 }}>{labelOf(sectionId)}</span>
                  {!locked && (
                    <>
                      <button className="btn small ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up">↑</button>
                      <button className="btn small ghost" disabled={i === draft.pageOrder.length - 1} onClick={() => move(i, 1)} aria-label="Move down">↓</button>
                    </>
                  )}
                </div>
              ))}
              <div className="order-item fixed">Closing page</div>
            </div>
          </div>
        </div>

        <div className="sticky">
          <div className="sub" style={{ marginBottom: 6 }}>Preview with the Garmisch sample {dirty && '· unsaved changes'}</div>
          {debouncedSrc && <iframe key={debouncedSrc} className="preview-frame tall" title="Template preview" src={debouncedSrc} />}
        </div>
      </div>
    </>
  );
}
